import React, { useId, useMemo } from "react";
import { Box, Flex, HStack, Text } from "@chakra-ui/react";

function formatLitros(n) {
  return Number(n).toLocaleString("pt-BR");
}

function getCapacities(veiculo) {
  if (!veiculo) return { caps: [], total: 0, numBocas: 0 };
  const arr = Array.isArray(veiculo.litros_por_boca)
    ? veiculo.litros_por_boca
    : [];
  const n = Math.max(Number(veiculo.bocas) || 0, arr.length);
  const caps = Array.from({ length: n }, (_, i) => Number(arr[i]) || 0);
  return {
    caps,
    total: caps.reduce((s, c) => s + c, 0),
    numBocas: n,
  };
}

function computeVolumesPorBoca(
  itensOrdem,
  compartimentosSel,
  volumeAlocar,
  veiculoId = null,
) {
  const allocated = {};
  for (const item of itensOrdem) {
    if (veiculoId && item.veiculoId && item.veiculoId !== veiculoId) continue;
    const vol = Number(item.volume) || 0;
    const comps = item.compartimentos || [];
    if (!comps.length) continue;
    const perBoca = vol / comps.length;
    for (const n of comps) {
      allocated[n] = (allocated[n] || 0) + perBoca;
    }
  }

  const preview = {};
  const vol = Number(volumeAlocar) || 0;
  if (vol > 0 && compartimentosSel.length > 0) {
    const perBoca = vol / compartimentosSel.length;
    for (const n of compartimentosSel) {
      preview[n] = perBoca;
    }
  }

  return { allocated, preview };
}

const Legend = ({ showIncompatible = false, showFill = false }) => (
  <HStack spacing={5} mb={4} fontSize="xs" color="gray.600" flexWrap="wrap">
    <HStack spacing={2}>
      <Box w={3} h={3} borderRadius="full" bg="#c0a316" />
      <Text>Selecionado</Text>
    </HStack>
    {showFill && (
      <HStack spacing={2}>
        <Box w={3} h={3} borderRadius="sm" bg="#007abe" opacity={0.85} />
        <Text>Preenchido</Text>
      </HStack>
    )}
    {showFill && (
      <HStack spacing={2}>
        <Box w={3} h={3} borderRadius="sm" bg="#f59e0b" opacity={0.85} />
        <Text>Prévia da seleção</Text>
      </HStack>
    )}
    <HStack spacing={2}>
      <Box w={3} h={3} borderRadius="full" bg="#94a3b8" />
      <Text>Alocado</Text>
    </HStack>
    <HStack spacing={2}>
      <Box
        w={3}
        h={3}
        border="2px dashed"
        borderColor="#94a3b8"
        borderRadius="sm"
        bg="white"
      />
      <Text>Disponível</Text>
    </HStack>
    {showIncompatible && (
      <HStack spacing={2}>
        <Box
          w={3}
          h={3}
          border="2px solid"
          borderColor="#ef4444"
          borderRadius="sm"
          bg="#fef2f2"
        />
        <Text>Volume excede a boca</Text>
      </HStack>
    )}
    <HStack spacing={2}>
      <Box w={3} h={3} borderRadius="sm" bg="#007abe" opacity={0.7} />
      <Text>Capacidade máx.</Text>
    </HStack>
  </HStack>
);

/**
 * Visual do caminhão-tanque no estilo dashboard (compartimentos verticais no cilindro).
 * mode: "capacity" (somente leitura) | "interactive" (seleção nas etapas de carga)
 */
const TruckCompartmentsVisual = ({
  veiculo,
  mode = "capacity",
  compartimentosSel = [],
  itensOrdem = [],
  volumeAlocar = 0,
  onToggleCompartimento,
  onIncompatibleClick,
  showLegend = true,
}) => {
  const uid = useId().replace(/:/g, "");
  const { caps, total, numBocas } = useMemo(
    () => getCapacities(veiculo),
    [veiculo],
  );

  const { allocated: volAlloc, preview: volPreview } = useMemo(
    () =>
      computeVolumesPorBoca(
        itensOrdem,
        compartimentosSel,
        volumeAlocar,
        veiculo?.id || null,
      ),
    [itensOrdem, compartimentosSel, volumeAlocar, veiculo?.id],
  );

  const itensDoVeiculo = useMemo(() => {
    const id = veiculo?.id;
    if (!id) return itensOrdem;
    return itensOrdem.filter((it) => !it.veiculoId || it.veiculoId === id);
  }, [itensOrdem, veiculo?.id]);

  const hasFill =
    Object.keys(volAlloc).length > 0 || Object.keys(volPreview).length > 0;

  if (!veiculo || numBocas === 0) {
    return (
      <Box
        p={8}
        textAlign="center"
        bg="white"
        borderRadius="xl"
        border="1px dashed"
        borderColor="gray.300"
      >
        <Text fontSize="sm" color="gray.500">
          Selecione um veículo para visualizar os compartimentos
        </Text>
      </Box>
    );
  }

  const isInteractive = mode === "interactive";
  const volAlocar = Number(volumeAlocar) || 0;

  const isIncompatible = (n) => {
    if (!isInteractive || volAlocar <= 0) return false;
    const cap = caps[n - 1] ?? 0;
    return volAlocar > cap;
  };

  const hasIncompatible =
    isInteractive && volAlocar > 0 && caps.some((cap) => volAlocar > cap);

  const padX = 20;
  const tankH = 112;
  const tankY = 28;
  const cellW = Math.max(56, Math.min(78, 720 / numBocas));
  const tankW = numBocas * cellW;
  const tankX = padX;
  const svgW = tankX + tankW + padX;
  const chassisY = tankY + tankH + 8;
  const volLabelY = chassisY + 18;
  const volMaxY = chassisY + 32;
  const svgH = volMaxY + 16;

  const getCompState = (n) => {
    const alocado = itensDoVeiculo.some((it) => it.compartimentos.includes(n));
    const selecionado = compartimentosSel.includes(n);
    if (selecionado) return "selected";
    if (alocado) return "allocated";
    if (isIncompatible(n)) return "incompatible";
    return "available";
  };

  const handleCellClick = (n) => {
    if (!isInteractive) return;
    if (isIncompatible(n)) {
      onIncompatibleClick?.(n, caps[n - 1] ?? 0);
      return;
    }
    if (itensDoVeiculo.some((it) => it.compartimentos.includes(n))) return;
    onToggleCompartimento?.(n);
  };

  return (
    <Box
      bg="white"
      borderRadius="xl"
      border="1px solid"
      borderColor="gray.200"
      p={{ base: 4, md: 5 }}
      boxShadow="sm"
    >
      {showLegend && (
        <Legend
          showIncompatible={hasIncompatible}
          showFill={isInteractive && hasFill}
        />
      )}

      <Box overflowX="auto" pb={1}>
        <svg
          viewBox={`0 0 ${svgW} ${svgH}`}
          width="100%"
          style={{
            minWidth: Math.min(svgW, 900),
            maxHeight: 280,
            display: "block",
          }}
          role="img"
          aria-label={`Tanque ${veiculo.placa || ""}, ${numBocas} compartimentos`}
        >
          <defs>
            <linearGradient id={`${uid}-fuel`} x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#005a8a" />
              <stop offset="100%" stopColor="#38a3d4" />
            </linearGradient>
            <linearGradient id={`${uid}-preview`} x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#b45309" />
              <stop offset="100%" stopColor="#fbbf24" />
            </linearGradient>
            <clipPath id={`${uid}-tank-clip`}>
              <rect
                x={tankX}
                y={tankY}
                width={tankW}
                height={tankH}
                rx={tankH / 2}
              />
            </clipPath>
          </defs>

          {/* Corpo do tanque (cilindro) — só a carga */}
          <rect
            x={tankX}
            y={tankY}
            width={tankW}
            height={tankH}
            rx={tankH / 2}
            fill="#f8fafc"
            stroke="#94a3b8"
            strokeWidth={2.5}
          />
          <ellipse
            cx={tankX + 8}
            cy={tankY + tankH / 2}
            rx={8}
            ry={tankH / 2 - 6}
            fill="#e2e8f0"
            stroke="#94a3b8"
            strokeWidth={1.5}
          />
          <ellipse
            cx={tankX + tankW - 8}
            cy={tankY + tankH / 2}
            rx={8}
            ry={tankH / 2 - 6}
            fill="#e2e8f0"
            stroke="#94a3b8"
            strokeWidth={1.5}
          />

          <g clipPath={`url(#${uid}-tank-clip)`}>
            {caps.map((cap, i) => {
              const n = i + 1;
              const x = tankX + i * cellW;
              const state = isInteractive
                ? getCompState(n)
                : "capacity";
              const capBoca = cap || 1;
              const innerPad = 6;
              // Altura do tanque = célula inteira; o nível do líquido é
              // vol / capacidade desta boca (não relativo ao maior compartimento).
              const innerH = tankH - innerPad * 2;
              const fillY = tankY + innerPad;

              const volNaBoca = volAlloc[n] || 0;
              const volPrevNaBoca = volPreview[n] || 0;
              const allocRatio = Math.min(1, volNaBoca / capBoca);
              const totalPreviewRatio = Math.min(
                1,
                (volNaBoca + volPrevNaBoca) / capBoca,
              );
              const allocH =
                volNaBoca > 0 ? Math.max(3, allocRatio * innerH) : 0;
              const previewH =
                volPrevNaBoca > 0
                  ? Math.max(3, totalPreviewRatio * innerH) - allocH
                  : 0;
              const allocY = fillY + innerH - allocH;
              const previewY = allocY - previewH;

              let stroke = "#cbd5e1";
              let strokeDash = "none";
              let cellBg = "#ffffff";
              let labelFill = "#334155";
              let cursor = isInteractive ? "pointer" : "default";
              if (state === "selected") {
                stroke = "#c0a316";
                cellBg = "#fffbeb";
                labelFill = "#92400e";
              } else if (state === "allocated") {
                stroke = "#94a3b8";
                cellBg = "#f1f5f9";
              } else if (state === "incompatible") {
                stroke = "#ef4444";
                strokeDash = "5 3";
                cellBg = "#fef2f2";
                labelFill = "#b91c1c";
                cursor = "not-allowed";
              } else if (state === "available") {
                stroke = "#e2e8f0";
                strokeDash = "4 3";
              }

              const titleParts = [`C${n}: capacidade ${formatLitros(capBoca)} L`];
              if (volNaBoca > 0) {
                titleParts.push(`preenchido ${formatLitros(volNaBoca)} L`);
              }
              if (volPrevNaBoca > 0) {
                titleParts.push(`prévia +${formatLitros(volPrevNaBoca)} L`);
              }
              if (state === "incompatible") {
                titleParts.push(
                  `volume ${formatLitros(volAlocar)} L excede capacidade`,
                );
              }
              const titleText = titleParts.join(" · ");

              return (
                <g
                  key={n}
                  style={{ cursor }}
                  onClick={() => handleCellClick(n)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleCellClick(n);
                    }
                  }}
                  role={isInteractive ? "button" : undefined}
                  tabIndex={isInteractive && state !== "incompatible" && state !== "allocated" ? 0 : undefined}
                  aria-disabled={state === "incompatible" || state === "allocated"}
                >
                  <title>{titleText}</title>
                  <rect
                    x={x + 1}
                    y={tankY + 1}
                    width={cellW - 2}
                    height={tankH - 2}
                    fill={cellBg}
                  />
                  {isInteractive ? (
                    <>
                      <rect
                        x={x + 4}
                        y={fillY}
                        width={cellW - 8}
                        height={innerH}
                        rx={3}
                        fill="#e0f2fe"
                        opacity={0.6}
                      />
                      {allocH > 0 && (
                        <rect
                          x={x + 4}
                          y={allocY}
                          width={cellW - 8}
                          height={allocH}
                          rx={3}
                          fill={`url(#${uid}-fuel)`}
                          opacity={0.9}
                        />
                      )}
                      {previewH > 0 && (
                        <rect
                          x={x + 4}
                          y={previewY}
                          width={cellW - 8}
                          height={previewH}
                          rx={3}
                          fill={`url(#${uid}-preview)`}
                          opacity={0.85}
                        />
                      )}
                    </>
                  ) : (
                    <rect
                      x={x + 4}
                      y={fillY}
                      width={cellW - 8}
                      height={innerH}
                      rx={3}
                      fill={
                        state === "incompatible"
                          ? "#fecaca"
                          : `url(#${uid}-fuel)`
                      }
                      opacity={
                        state === "incompatible"
                          ? 0.5
                          : state === "allocated"
                            ? 0.45
                            : 0.85
                      }
                    />
                  )}
                  {state === "incompatible" && isInteractive && (
                    <>
                      <line
                        x1={x + 8}
                        y1={tankY + 12}
                        x2={x + cellW - 8}
                        y2={tankY + tankH - 12}
                        stroke="#ef4444"
                        strokeWidth={2}
                        opacity={0.55}
                        pointerEvents="none"
                      />
                      <line
                        x1={x + cellW - 8}
                        y1={tankY + 12}
                        x2={x + 8}
                        y2={tankY + tankH - 12}
                        stroke="#ef4444"
                        strokeWidth={2}
                        opacity={0.55}
                        pointerEvents="none"
                      />
                    </>
                  )}
                  {i > 0 && (
                    <line
                      x1={x}
                      y1={tankY + 4}
                      x2={x}
                      y2={tankY + tankH - 4}
                      stroke="#94a3b8"
                      strokeWidth={1.5}
                    />
                  )}
                  <rect
                    x={x + 2}
                    y={tankY + 2}
                    width={cellW - 4}
                    height={tankH - 4}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={state === "selected" ? 2.5 : 1}
                    strokeDasharray={strokeDash}
                    rx={2}
                    pointerEvents="none"
                  />
                  <text
                    x={x + cellW / 2}
                    y={tankY + tankH / 2 + (volNaBoca > 0 ? 0 : 6)}
                    textAnchor="middle"
                    fontSize={15}
                    fontWeight="700"
                    fill={labelFill}
                    pointerEvents="none"
                  >
                    C{n}
                  </text>
                  {isInteractive && volNaBoca > 0 && (
                    <text
                      x={x + cellW / 2}
                      y={tankY + tankH / 2 + 16}
                      textAnchor="middle"
                      fontSize={9}
                      fontWeight="700"
                      fill="#005a8a"
                      pointerEvents="none"
                    >
                      {formatLitros(Math.round(volNaBoca))} L
                    </text>
                  )}
                  {isInteractive && volPrevNaBoca > 0 && volNaBoca === 0 && (
                    <text
                      x={x + cellW / 2}
                      y={tankY + tankH / 2 + 16}
                      textAnchor="middle"
                      fontSize={9}
                      fontWeight="600"
                      fill="#b45309"
                      pointerEvents="none"
                    >
                      +{formatLitros(Math.round(volPrevNaBoca))} L
                    </text>
                  )}
                  {state === "incompatible" && (
                    <text
                      x={x + cellW / 2}
                      y={tankY + tankH / 2 + 22}
                      textAnchor="middle"
                      fontSize={9}
                      fontWeight="600"
                      fill="#dc2626"
                      pointerEvents="none"
                    >
                      excede
                    </text>
                  )}
                </g>
              );
            })}
          </g>

          {/* Tampas no topo */}
          {caps.map((_, i) => {
            const x = tankX + i * cellW + cellW / 2;
            return (
              <g key={`cap-${i}`}>
                <ellipse
                  cx={x}
                  cy={tankY + 8}
                  rx={11}
                  ry={5}
                  fill="#cbd5e1"
                  stroke="#94a3b8"
                  strokeWidth={1.2}
                />
                <rect
                  x={x - 3}
                  y={tankY + 4}
                  width={6}
                  height={6}
                  rx={1}
                  fill="#94a3b8"
                />
              </g>
            );
          })}

          {/* Chassi / estrado */}
          <rect
            x={padX - 4}
            y={chassisY - 4}
            width={tankW + 8}
            height={10}
            rx={3}
            fill="#475569"
          />
          <rect
            x={tankX}
            y={chassisY - 1}
            width={tankW}
            height={4}
            fill="#64748b"
          />

          {/* Volume máx. por boca — alinhado ao centro de cada compartimento */}
          {caps.map((cap, i) => {
            const cx = tankX + i * cellW + cellW / 2;
            const n = i + 1;
            const blocked = isIncompatible(n);
            const volNaBoca = volAlloc[n] || 0;
            const volPrevNaBoca = volPreview[n] || 0;
            const capBoca = cap || 0;
            const hasVol = volNaBoca > 0 || volPrevNaBoca > 0;
            return (
              <g key={`vol-label-${i}`}>
                {hasVol && isInteractive ? (
                  <>
                    <text
                      x={cx}
                      y={volLabelY}
                      textAnchor="middle"
                      fontSize={12}
                      fontWeight="700"
                      fill="#005a8a"
                    >
                      {formatLitros(Math.round(volNaBoca + volPrevNaBoca))} L
                    </text>
                    <text
                      x={cx}
                      y={volMaxY}
                      textAnchor="middle"
                      fontSize={10}
                      fill="#64748b"
                    >
                      de {formatLitros(capBoca)} L
                    </text>
                  </>
                ) : (
                  <>
                    <text
                      x={cx}
                      y={volLabelY}
                      textAnchor="middle"
                      fontSize={13}
                      fontWeight="700"
                      fill={blocked ? "#dc2626" : "#007abe"}
                    >
                      {formatLitros(capBoca)} L
                    </text>
                    <text
                      x={cx}
                      y={volMaxY}
                      textAnchor="middle"
                      fontSize={10}
                      fill={blocked ? "#dc2626" : "#64748b"}
                    >
                      {blocked ? "insuficiente" : "máx."}
                    </text>
                  </>
                )}
              </g>
            );
          })}
        </svg>
      </Box>

      <Flex
        mt={4}
        pt={3}
        borderTop="1px solid"
        borderColor="gray.100"
        justify="space-between"
        align="center"
        flexWrap="wrap"
        gap={2}
      >
        <Text fontSize="sm" color="gray.600">
          {veiculo.placa}
          {veiculo.tipo ? ` · ${veiculo.tipo}` : ""}
        </Text>
        <Text fontSize="sm">
          <Text as="span" color="gray.500">
            Capacidade total:{" "}
          </Text>
          <Text as="span" fontWeight="bold" color="#007abe">
            {formatLitros(total)} L
          </Text>
          {isInteractive && hasFill && (
            <>
              {" · "}
              <Text as="span" color="gray.500">
                Carregado:{" "}
              </Text>
              <Text as="span" fontWeight="bold" color="#005a8a">
                {formatLitros(
                  Math.round(
                    Object.values(volAlloc).reduce((s, v) => s + v, 0),
                  ),
                )}{" "}
                L
              </Text>
            </>
          )}
        </Text>
      </Flex>
    </Box>
  );
};

export default TruckCompartmentsVisual;
